import { useEffect, useState } from "react";
import { FileText, FileImage, Sparkles, Loader2, Eye } from "lucide-react";
import { Button } from "./ui/button";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

function relBadge(rel) {
    if (rel === "red") return "bg-rose-100 text-rose-800";
    if (rel === "green") return "bg-emerald-100 text-emerald-800";
    return "bg-amber-100 text-amber-800";
}

export default function AdminDocumentsList({ userId }) {
    const [docs, setDocs] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/admin/documents", {
                    params: userId ? { user_id: userId } : {},
                });
                setDocs(data || []);
            } catch (e) {
                toast.error(formatApiError(e, "Dokumente nicht geladen"));
            } finally {
                setLoading(false);
            }
        })();
    }, [userId]);

    const openDoc = async (d) => {
        if (!d.released_to_advisor) {
            toast.error("Originaldokument wurde vom Nutzer nicht zur Einsicht freigegeben.");
            return;
        }
        try {
            const res = await api.get(`/documents/${d.document_id}/download`, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            window.open(url, "_blank");
            setTimeout(() => URL.revokeObjectURL(url), 60_000);
        } catch (e) {
            toast.error(formatApiError(e, "Download fehlgeschlagen"));
        }
    };

    if (loading) return <div className="p-8 text-center text-sm text-ink-500"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></div>;
    if (docs.length === 0) return <div className="p-6 text-center text-sm text-ink-500">Keine Dokumente vorhanden.</div>;

    return (
        <div className="space-y-3">
            {docs.map((d) => (
                <div key={d.document_id} className="rounded-2xl border border-ink-300/30 bg-white p-5" data-testid={`admin-doc-${d.document_id}`}>
                    <div className="flex flex-wrap items-center gap-3">
                        <span className="inline-flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-brand-50 text-brand-600">
                            {d.content_type?.startsWith("image/") ? <FileImage className="h-5 w-5" /> : <FileText className="h-5 w-5" />}
                        </span>
                        <div className="min-w-0 flex-1">
                            <div className="truncate font-display text-sm font-semibold text-ink-900">{d.original_filename}</div>
                            <div className="text-xs text-ink-500">
                                Nutzer: {d.user?.email || d.user_id} · {new Date(d.created_at).toLocaleString("de-DE")}
                            </div>
                        </div>
                        {d.document_type && (
                            <span className="rounded-full bg-brand-50 px-2.5 py-1 text-xs font-semibold text-brand-700">
                                {d.document_type}
                            </span>
                        )}
                        {d.relevance && (
                            <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${relBadge(d.relevance)}`}>
                                {d.relevance === "red" ? "Hohe Relevanz" : d.relevance === "green" ? "Geringe Relevanz" : "Zu beachten"}
                            </span>
                        )}
                        <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${d.released_to_advisor ? "bg-emerald-100 text-emerald-800" : "bg-ink-200/60"}`}>
                            {d.released_to_advisor ? "Freigegeben" : "Nicht freigegeben"}
                        </span>
                    </div>

                    {d.analysis && (
                        <div className="mt-3 rounded-xl bg-brand-50/40 p-3 text-sm text-ink-700">
                            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-brand-700">
                                <Sparkles className="h-3.5 w-3.5" /> KI-Voranalyse
                            </div>
                            <p className="mt-1.5">{d.analysis.summary}</p>
                            {d.analysis.advisor_notes && (
                                <p className="mt-2 rounded-lg bg-white p-2 text-xs text-ink-700">
                                    <strong>Hinweis: </strong>{d.analysis.advisor_notes}
                                </p>
                            )}
                            {Object.keys(d.analysis.extracted_entities || {}).length > 0 && (
                                <details className="mt-2">
                                    <summary className="cursor-pointer text-xs font-semibold text-brand-700">Extrahierte Daten</summary>
                                    <dl className="mt-2 grid gap-1.5 sm:grid-cols-2">
                                        {Object.entries(d.analysis.extracted_entities).map(([k, v]) => (
                                            <div key={k} className="rounded bg-white p-2 text-xs">
                                                <dt className="text-[10px] uppercase tracking-wider text-ink-500">{k.replace(/_/g, " ")}</dt>
                                                <dd className="font-medium text-ink-900">{String(v)}</dd>
                                            </div>
                                        ))}
                                    </dl>
                                </details>
                            )}
                        </div>
                    )}

                    <div className="mt-3">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openDoc(d)}
                            disabled={!d.released_to_advisor}
                            className="rounded-full gap-2"
                            data-testid={`admin-doc-open-${d.document_id}`}
                        >
                            <Eye className="h-3.5 w-3.5" /> Original öffnen
                        </Button>
                    </div>
                </div>
            ))}
        </div>
    );
}
